/**
 * Fetches every block on the School Analytics screen.
 *
 * Split into four effects so a filter only refetches what it actually changes:
 * the date window reloads the whole screen, while the book sort, the trend
 * metric and the school table each reload on their own.
 */

import { useCallback, useEffect, useState } from "react";
import {
  getAssignmentCountService,
  getHealthStatService,
  getNobodyAssigningService,
  getOverallStatService,
  getReaderSchoolsService,
  getReadingGrowthTrendService,
  getReadingLevelService,
  getReadingMixService,
  getRegularReaderService,
  getSchoolDashboardService,
  getSeatFunnelService,
  getTopBooksService,
} from "shared/services/schoolStatService";
import type { StatRange } from "shared/services/schoolStatService";
import type {
  AssignmentCount,
  DashboardStats,
  GrowthTrend,
  HealthStat,
  NobodyAssigning,
  OverallStat,
  ReaderSchools,
  ReadingLevel,
  ReadingMix,
  RegularReader,
  SeatFunnel,
  TopBooks,
} from "./adapters";

interface Args extends StatRange {
  /** by_read | by_complete */
  bookSort: string;
  /** book_read | cumulative_reading | engagement */
  trendMetric: string;
  /** all | demo | premium | expired | test */
  accountFilter: string;
  /** all | active | expired | paused */
  statusFilter: string;
  /** 0-based, as the table pager reports it. */
  rankPage: number;
  rankPageSize: number;
  /**
   * False while the range is incomplete — a custom preset with only one date
   * picked. Every effect then holds whatever it already fetched instead of
   * firing a call the user has not asked for yet.
   */
  enabled?: boolean;
}

/** Every payload is optional: a failed call just leaves its block empty. */
interface CoreData {
  overall?: OverallStat;
  dashboard?: DashboardStats;
  health?: HealthStat;
  funnel?: SeatFunnel;
  silent?: NobodyAssigning;
  mix?: ReadingMix;
  regular?: RegularReader;
  level?: ReadingLevel;
  assignment?: AssignmentCount;
}

/** Resolves to undefined instead of rejecting, so one dead call can't blank the screen. */
const safe = <T,>(request: Promise<any>): Promise<T | undefined> =>
  request
    .then((response) => (response?.data?.data as T) ?? undefined)
    .catch(() => undefined);

function useSchoolAnalytics({
  from,
  to,
  bookSort,
  trendMetric,
  accountFilter,
  statusFilter,
  rankPage,
  rankPageSize,
  enabled = true,
}: Args) {
  const [core, setCore] = useState<CoreData>({});
  const [coreLoading, setCoreLoading] = useState<boolean>(true);

  const [books, setBooks] = useState<TopBooks | undefined>();
  const [booksLoading, setBooksLoading] = useState<boolean>(true);

  const [trend, setTrend] = useState<GrowthTrend | undefined>();
  const [trendLoading, setTrendLoading] = useState<boolean>(true);

  const [reader, setReader] = useState<ReaderSchools | undefined>();
  const [readerLoading, setReaderLoading] = useState<boolean>(true);

  /** Bumped by refetch(); every effect below watches it. */
  const [reloadKey, setReloadKey] = useState<number>(0);
  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  // Account info, headline figures, funnel and reading mix.
  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const range: StatRange = { from, to };
    setCoreLoading(true);

    Promise.all([
      safe<OverallStat>(getOverallStatService()),
      safe<{ stats: DashboardStats }>(getSchoolDashboardService(range)),
      safe<HealthStat>(getHealthStatService(range)),
      safe<SeatFunnel>(getSeatFunnelService(range)),
      safe<NobodyAssigning>(getNobodyAssigningService(range)),
      safe<ReadingMix>(getReadingMixService(range)),
      safe<RegularReader>(getRegularReaderService(range)),
      safe<ReadingLevel>(getReadingLevelService(range)),
      safe<AssignmentCount>(getAssignmentCountService(range)),
    ]).then(
      ([overall, dashboard, health, funnel, silent, mix, regular, level, assignment]) => {
        if (cancelled) return;
        setCore({
          overall,
          // The dashboard endpoint nests its six counts under `stats`.
          dashboard: dashboard?.stats,
          health,
          funnel,
          silent,
          mix,
          regular,
          level,
          assignment,
        });
        setCoreLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [from, to, reloadKey, enabled]);

  // Top books: also reloads on its own sort.
  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setBooksLoading(true);

    safe<TopBooks>(getTopBooksService({ from, to }, bookSort)).then((data) => {
      if (cancelled) return;
      setBooks(data);
      setBooksLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [from, to, bookSort, reloadKey, enabled]);

  // Reading trends: also reloads on its own metric.
  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setTrendLoading(true);

    safe<GrowthTrend>(
      getReadingGrowthTrendService({ from, to }, trendMetric)
    ).then((data) => {
      if (cancelled) return;
      setTrend(data);
      setTrendLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [from, to, trendMetric, reloadKey, enabled]);

  // Reading by school: the account and status filters and paging are all
  // server-side.
  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setReaderLoading(true);

    safe<ReaderSchools>(
      getReaderSchoolsService(
        { from, to },
        accountFilter,
        rankPage + 1,
        rankPageSize,
        statusFilter
      )
    ).then((data) => {
      if (cancelled) return;
      setReader(data);
      setReaderLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    from,
    to,
    accountFilter,
    statusFilter,
    rankPage,
    rankPageSize,
    reloadKey,
    enabled,
  ]);

  return {
    core,
    coreLoading,
    books,
    booksLoading,
    trend,
    trendLoading,
    reader,
    readerLoading,
    refetch,
  };
}

export default useSchoolAnalytics;
